// 🎬🗂 VM 한쪽 패널 자리 계산(순수 함수) — 판정은 `lib/templates/vm-side-panel.ts`, 그리기는 `Overlay169SlideLayer`.
//   참고 영상(안전교육 가로 · 10-07 프레임 실측 1920×1080): 글자 줄 왼쪽 x≈1115(58%) · 꼬리표 위 y≈120 · 제목 64px · 항목 40px ·
//   화면 왼쪽 절반은 덮개 없이 또렷 · 오른쪽 약 40% 만 짙다.
//   ★장표 10종은 손대지 않는다★ — 장표를 «축소한 가상 화면» 안에 그대로 그린다(프레젠터 예약 컬럼과 같은 «상자 기준 재해석» 길).
//     가상 화면 = (글자 줄 폭 ÷ k + 세이프존 좌우) × (남은 높이 ÷ k + 세이프존 위아래) · transform scale(k) 한 번.
//     장표가 쓰는 세이프존 여백(useSafeArea)이 가상 화면 안쪽 여백이 되고, k 배로 줄어 글자 줄 정확히 그 자리에 놓인다.
//   ★k 는 글이 남은 높이에 들어가는 가장 큰 값★(상한 0.72 = 참고 영상 항목 40px ≈ 본문 56px × 0.72 · 하한 0.5) — 어림은 모션 브리핑 무대와 같은 식(`briefContentHeight`).
//   내보내기 엔진이 그리는 것만 쓴다(지원표 §13-11): linear-gradient 한 겹 · 2D scale · 둥근 모서리 · 배경색. blur·textShadow·backdrop 없음.
import { safeInsetPx } from '../../lib/safe-zone';
import { briefContentHeight, type BriefSlide } from '../msb/motion-brief-shape';

/** 1920 기준 수치 — 참고 영상 실측에서 왔다. */
export const VM_PANEL = Object.freeze({
  /** 오른쪽 패널의 글자 줄 왼쪽(화면 폭 비율) — 참고 x 1115/1920. 왼쪽 패널(프레젠터가 오른쪽을 쓸 때)은 1 − 이 값이 글자 줄 오른쪽. */
  textEdge: 0.58,
  maxScale: 0.72,
  minScale: 0.5,
  /** 꼬리표 — 글자 30px · 위아래 8 · 좌우 16 · 모서리 8(알약 모양 아님) · 꼬리표↔장표 24. */
  tagPx: 30, tagPadY: 8, tagPadX: 16, tagRadius: 8, tagGap: 24,
  /** 자막 막대 위 여유. */
  captionGap: 32,
  /** 어림이 실제보다 짧을 때를 위한 여유(4%). */
  fitSlack: 1.04,
});

/** 그라데이션 정지점(패널 쪽 가장자리에서 잰 화면 폭 %) · 진하기. 반대쪽 40% 는 완전 투명. */
export const VM_PANEL_SCRIM = Object.freeze({ deep: 0.84, mid: 0.76, midAt: 36, clearAt: 60 });

/** 아래쪽에 앉는 장표 — 한 문장 장면(영상 위 «한쪽 아래» 글자). 나머지는 꼬리표 바로 아래부터 위에서 내려온다. */
const BOTTOM_TYPES: ReadonlySet<string> = new Set(['fullText', 'kineticTypo']);

export interface VmPanelLayout {
  side: 'right' | 'left';
  scale: number;
  /** 가상 화면(축소 전 크기 · 화면 px) — transform: scale(scale) · origin 0 0. */
  frame: { left: number; top: number; width: number; height: number };
  /** 꼬리표 왼쪽 위(화면 px) · 글자 크기 등은 `VM_PANEL` × u. 꼬리표가 없으면 null. */
  tag: { left: number; top: number; fontPx: number; padY: number; padX: number; radius: number } | null;
  /** 장표가 놓일 자리(화면 px) — 검사·측정용. */
  text: { left: number; right: number; top: number; bottom: number };
  /** 장표 정렬 — 왼쪽 정렬(참고 영상). 한 문장 장면의 «아래쪽»은 `text.top` 을 내려서 만든다(꼬리표가 제목 바로 위에 붙게). */
  anchor: 'top-left';
}

export function vmPanelLayout(o: {
  width: number; height: number; side: 'right' | 'left'; hasTag: boolean; captionTop?: number; slide: BriefSlide;
}): VmPanelLayout {
  const u = o.width / 1920, H = o.height / u; // 1920 기준 좌표로 계산하고 마지막에 u 배
  const safe = safeInsetPx('16:9', 1920, H, 'core');
  const xL = o.side === 'right' ? 1920 * VM_PANEL.textEdge : safe.left;
  const xR = o.side === 'right' ? 1920 - safe.right : 1920 * (1 - VM_PANEL.textEdge);
  const colW = xR - xL;
  const tagH = VM_PANEL.tagPx * 1.2 + 2 * VM_PANEL.tagPadY;
  const yTop = safe.top;
  const captionTop = typeof o.captionTop === 'number' && Number.isFinite(o.captionTop) && o.captionTop > 0 ? o.captionTop / u : undefined;
  const yB = Math.min(H - safe.bottom * 0.5, captionTop !== undefined ? captionTop - VM_PANEL.captionGap : H - safe.bottom);
  const firstTop = o.hasTag ? yTop + tagH + VM_PANEL.tagGap : yTop;
  const avail = Math.max(1, yB - firstTop);
  const heightAt = (k: number) => (briefContentHeight(o.slide, colW / k) ?? 0) * k * VM_PANEL.fitSlack;
  let k = VM_PANEL.minScale;
  for (let s = VM_PANEL.maxScale; s >= VM_PANEL.minScale - 1e-9; s -= 0.02) {
    if (heightAt(s) <= avail) { k = Math.round(s * 100) / 100; break; }
  }
  const bottom = BOTTOM_TYPES.has(o.slide.type);
  const textTop = bottom ? Math.max(firstTop, yB - heightAt(k)) : firstTop;
  const tagTop = bottom ? textTop - VM_PANEL.tagGap - tagH : yTop;
  return {
    side: o.side,
    scale: k,
    frame: {
      left: (xL - safe.left * k) * u,
      top: (textTop - safe.top * k) * u,
      width: (colW / k + safe.left + safe.right) * u,
      height: ((yB - textTop) / k + safe.top + safe.bottom) * u,
    },
    tag: o.hasTag ? { left: xL * u, top: tagTop * u, fontPx: VM_PANEL.tagPx * u, padY: VM_PANEL.tagPadY * u, padX: VM_PANEL.tagPadX * u, radius: VM_PANEL.tagRadius * u } : null,
    text: { left: xL * u, right: xR * u, top: textTop * u, bottom: yB * u },
    anchor: 'top-left',
  };
}

/** 패널 쪽만 짙은 한 겹 그라데이션 — 색은 장표 바탕색(밝은 가족은 밝은 막 · 어두운 가족은 어두운 막). */
export function vmPanelScrim(side: 'right' | 'left', rgb: [number, number, number]): string {
  const c = (a: number) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;
  const { deep, mid, midAt, clearAt } = VM_PANEL_SCRIM;
  return `linear-gradient(to ${side === 'right' ? 'left' : 'right'}, ${c(deep)} 0%, ${c(mid)} ${midAt}%, ${c(0)} ${clearAt}%)`;
}
